import { z } from 'zod';

const bomPositionSchema = z.object({
  name: z.string().trim().min(1, '位置名稱不能為空').max(100, '位置名稱最多 100 字'),
  usageType: z.enum(['USED', 'NOT_USED']),
});

const bomMaterialSchema = z.object({
  mtrId: z.string().trim().min(1, '請選擇物料'),
  mtrNo: z.string().optional().or(z.literal('')),
  mtrName: z.string().optional().or(z.literal('')),
  positions: z.array(bomPositionSchema),
});

const bomGroupSchema = z.object({
  name: z.string().trim().min(1, '群組名稱不能為空').max(100, '群組名稱最多 100 字'),
  materials: z.array(bomMaterialSchema),
});

/**
 * 後端 numeric(18,6)：四捨五入到六位小數後須 > 0 且 < 1e12（be doc f77b08e9，
 * 超界回 BOM_EXTRA_MATERIAL_QTY_INVALID）。
 */
export function isBomExtraQtyStorable(value) {
  const qty = Number(value);
  if (!Number.isFinite(qty)) {
    return false;
  }
  if (qty < 0.0000005) {
    return false;
  }
  // 上界用字串判斷：12 位整數 + 六位小數已超過 double 精度
  const [rawInt = '', rawFrac = ''] = String(value).trim().replace(/^\+/, '').split('.');
  const integerPart = rawInt.replace(/^0+(?=\d)/, '');
  if (!/^\d+$/.test(integerPart) || !/^\d*$/.test(rawFrac)) {
    return qty < 1e12;
  }
  if (integerPart.length < 12) {
    return true;
  }
  if (integerPart.length > 12) {
    return false;
  }
  // 999999999999.9999995 起四捨五入會進位成 1e12
  const roundsUp = /^9{12}$/.test(integerPart)
    && /^9{6}/.test(rawFrac)
    && Number(rawFrac[6] ?? '0') >= 5;
  return !roundsUp;
}

const bomExtraMaterialSchema = z.object({
  mtrId: z.string().trim().min(1, '請選擇附加物料'),
  mtrNo: z.string().optional().or(z.literal('')),
  mtrName: z.string().optional().or(z.literal('')),
  qty: z.union([z.string(), z.number()])
    .refine(
      (value) => value !== '' && value != null && !Number.isNaN(Number(value)) && Number(value) > 0,
      { message: '數量須大於 0' },
    )
    .refine(
      (value) => Number.isNaN(Number(value)) || Number(value) <= 0 || isBomExtraQtyStorable(value),
      { message: '數量須 ≥ 0.0000005 且小於 1,000,000,000,000（六位小數）' },
    ),
});

export const bomFormSchema = z.object({
  code: z.string().trim().min(1, 'BOM代號不能為空').max(50, 'BOM代號最多 50 字'),
  name: z.string().trim().min(1, 'BOM名稱不能為空').max(100, 'BOM名稱最多 100 字'),
  version: z.string().trim().min(1, '版本號不能為空').max(50, '版本號最多 50 字'),
  remark: z.string().max(500, '備註最多 500 字').optional().or(z.literal('')),
  groups: z.array(bomGroupSchema),
  extraMaterials: z.array(bomExtraMaterialSchema),
  linkedMtrId: z.string().optional().or(z.literal('')),
  linkedMtrNo: z.string().optional().or(z.literal('')),
  linkedMtrName: z.string().optional().or(z.literal('')),
});

export const BOM_FIELD_LABELS = {
  code: 'BOM代號',
  name: 'BOM名稱',
  version: '版本號',
  remark: '備註',
  groups: '物料位置',
  extraMaterials: '附加物料',
  linkedMtrId: '關聯物料',
};

export function resolveBomFieldLabel(path) {
  if (!path) {
    return undefined;
  }
  if (path === 'groups') {
    return '物料位置';
  }
  if (path === 'extraMaterials') {
    return '附加物料';
  }
  if (/^groups\.(\d+)\.name$/.test(path)) {
    const groupIndex = Number(path.match(/^groups\.(\d+)\.name$/)[1]);
    return `群組名稱 #${groupIndex + 1}`;
  }
  if (/^groups\.\d+\.materials\.\d+\.mtrId$/.test(path)) {
    return '物料';
  }
  if (/^groups\.(\d+)\.materials\.(\d+)\.positions\.(\d+)\.name$/.test(path)) {
    const positionIndex = Number(path.match(
      /^groups\.(\d+)\.materials\.(\d+)\.positions\.(\d+)\.name$/,
    )[3]);
    return `位置名稱 #${positionIndex + 1}`;
  }
  if (/^groups\.\d+\.materials\.\d+\.positions\.\d+\.usageType$/.test(path)) {
    return '位置啟用';
  }
  if (/^extraMaterials\.\d+\.mtrId$/.test(path)) {
    return '附加物料';
  }
  if (/^extraMaterials\.\d+\.qty$/.test(path)) {
    return '附加物料數量';
  }
  return undefined;
}
